var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef } from 'react';
import { Input } from '../../input';
import { DISPLAY_DATE_FORMAT, ISO_DATE_FORMAT, USER_DATE_FORMAT, USER_SHORT_DATE_FORMAT, formatDate, safeParseDate, tryParseDate, } from '../helpers';
import { Trigger } from './Trigger';
/**
 * This component is used to render the input field of the date input. It has a display format and a format when editing.
 * To support this, the value is parsed and formatted when the input is focused and blurred.
 * To also support changes by the date picker, the value is set directly on the reference when changed.
 * [1] It was not possible to use the onChange for this, because the date is not always ready to be parsed.
 */
export const DateInputField = (_a) => {
    var { reference, value, name, disabled, color, colorContext, changeHandler, showDatepickerOnFocus, showDayPicker } = _a, props = __rest(_a, ["reference", "value", "name", "disabled", "color", "colorContext", "changeHandler", "showDatepickerOnFocus", "showDayPicker"]);
    const inputRef = useRef(null);
    /**
     * When the input is focused, the value is parsed and formatted to the user format.
     */
    const handleInputFocus = (event) => {
        const element = event.target;
        const date = safeParseDate(element.value, DISPLAY_DATE_FORMAT);
        if (date !== undefined) {
            element.value = formatDate(date, USER_DATE_FORMAT);
        }
        if (showDatepickerOnFocus) {
            showDayPicker();
        }
    };
    /**
     * When the input is blurred, the value is parsed and formatted to the user format. The value is also parsed to the
     * ISO format and passed to the change handler.
     */
    const handleInputBlur = (event) => {
        const element = event.target;
        const date = tryParseDate(element.value, [USER_DATE_FORMAT, USER_SHORT_DATE_FORMAT]);
        if (date !== undefined) {
            element.value = formatDate(date, DISPLAY_DATE_FORMAT);
            changeHandler(formatDate(date, ISO_DATE_FORMAT));
        }
        else {
            changeHandler(element.value);
        }
    };
    /**
     * When the value changes, the input value on the DOM element is updated.
     */
    useEffect(() => {
        if (inputRef.current === null) {
            return;
        }
        const date = safeParseDate(value, ISO_DATE_FORMAT);
        if (date !== undefined) {
            inputRef.current.value = formatDate(date, DISPLAY_DATE_FORMAT);
        }
        else if (value !== undefined) {
            inputRef.current.value = value;
        }
    }, [value]);
    const handleTriggerClick = () => {
        if (disabled) {
            return;
        }
        showDayPicker();
    };
    return (_jsxs(_Fragment, { children: [_jsx(Input, Object.assign({ ref: inputRef, reference: reference, name: `${name}-visual`, disabled: disabled, colorContext: colorContext, color: color, onFocus: handleInputFocus, onBlur: handleInputBlur }, props)), _jsx(Trigger, { onClick: handleTriggerClick, disabled: disabled, colorContext: colorContext })] }));
};
